import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Box, IconButton } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { X } from 'react-feather'
import { ModalLayout } from '@/components'
import {
  ControlledDateField,
  ResearchersMultiSelect,
  ControlledCheckboxField,
} from '@/components/inputs'
import * as yup from 'yup'
import { theme } from '@/theme'
import { useExpensesExport } from '@/hooks'
import dayjs from 'dayjs'
import { DATE_FORMAT_YMD } from '@/constants'

export type ExpensesExportFormData = {
  startDate: string | null
  endDate: string | null
  researchers: string | null
  isExported: boolean | null
}

const exportSchema = yup.object({
  startDate: yup.date().typeError('invalid_date').required(),
  endDate: yup.date().typeError('invalid_date').optional().nullable(),
  researchers: yup.string().optional().nullable(),
  isExported: yup.boolean().optional(),
})

export const ExpensesExportForm = (): JSX.Element => {
  const { t } = useTranslation('expensesDeclaration')
  const { enqueueSnackbar } = useSnackbar()

  const { exportExpenses, loading: isExportInProcess } = useExpensesExport()

  const defaultValues: ExpensesExportFormData = {
    startDate: null,
    endDate: null,
    researchers: null,
    isExported: false,
  }

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<ExpensesExportFormData>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(exportSchema),
  })

  const handleReset = () => reset({ ...defaultValues })

  const handleExportSubmit = async ({
    startDate,
    endDate,
    researchers,
    isExported,
  }: ExpensesExportFormData) => {
    const exportData = {
      startDate: startDate
        ? dayjs(startDate).format(DATE_FORMAT_YMD)
        : undefined,
      endDate: endDate ? dayjs(endDate).format(DATE_FORMAT_YMD) : undefined,
      researchers: researchers || undefined,
      isExported: isExported === null ? undefined : isExported,
    }

    try {
      await exportExpenses(exportData)
      enqueueSnackbar(t('common:exportedSuccessfully'), { variant: 'success' })
      handleReset()
    } catch (e) {
      enqueueSnackbar(`${t('errors:api.execute')}: ${e}` as string, {
        variant: 'error',
      })
    }
  }

  return (
    <ModalLayout title={t('titles.exportExpenses')}>
      <form onSubmit={handleSubmit(async (data) => handleExportSubmit(data))}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" spacing={4}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box width={220}>
                <ControlledDateField
                  name="startDate"
                  placeholder={t('forms:placeholders.start-date')}
                  control={control}
                  apiFieldName="startDate"
                />
              </Box>
              <Box width={220}>
                <ControlledDateField
                  name="endDate"
                  placeholder={t('forms:placeholders.end-date')}
                  control={control}
                  apiFieldName="endDate"
                />
              </Box>
              <Box>
                <ControlledCheckboxField
                  name="isExported"
                  control={control}
                  label={t('forms:placeholders.isExported')}
                />
              </Box>
            </Stack>
            <Box>
              <Controller
                name="researchers"
                control={control}
                render={({ field }) => (
                  <ResearchersMultiSelect
                    value={field.value as string}
                    onChange={field.onChange}
                    placeholder={t('placeholders.researcher')}
                  />
                )}
              />
            </Box>
            <Box sx={{ color: theme.palette.grey['600'], fontSize: 12 }}>
              {t('messages.courseTravelNotExported')}
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={5}>
            <Box mt={1} ml={4}>
              <IconButton
                onClick={handleReset}
                title={t('buttons.resetFilters')}
              >
                <X size="16px" color={theme.palette.green['500']} />
              </IconButton>
            </Box>
            <LoadingButton
              variant="contained"
              color="secondary"
              type="submit"
              loading={isExportInProcess}
              disableElevation={true}
              disabled={!isDirty}
            >
              <span>{t('buttons.export')}</span>
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </ModalLayout>
  )
}
